import { CurriculumLibrary } from '../../../components/v2/teacher/CurriculumLibrary';
import { useTeacherWorkspace } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { hooksV2Api, lessonPlansApi } from '../../../services/apiV2';
import { Hook, LessonPlan } from '../../../types/v2';
import TeacherNav from '../../../components/TeacherNav';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { PageHeader } from '../../../components/layout/PageHeader';
import { SectionCard } from '../../../components/layout/SectionCard';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';

import { StandaloneLessons } from '../../../components/v2/teacher/StandaloneLessons';

export const V2Plan: React.FC = () => {
  const workspace = useTeacherWorkspace();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'activities';
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewHook, setShowNewHook] = useState(false);
  const [newHookTitle, setNewHookTitle] = useState('');
  const [newHookDescription, setNewHookDescription] = useState('');
  const [savingHook, setSavingHook] = useState(false);
  const [showArchivedHooks, setShowArchivedHooks] = useState(false);

  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_plan');

  const normalizeArray = (data: any) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [hooksRes, plansRes] = await Promise.all([
          hooksV2Api.list(),
          lessonPlansApi.list(),
        ]);
        setHooks(normalizeArray(hooksRes.data));
        setLessonPlans(normalizeArray(plansRes.data).filter((plan: any) => !plan.curriculum_delivery));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getPlansForHook = (hookId: number) => lessonPlans.filter((plan) => plan.hook === hookId);

  const visibleHooks = hooks.filter((hook) => (showArchivedHooks ? true : !hook.is_archived));

  const handleCreateHook = async () => {
    if (!newHookTitle.trim()) return;
    setSavingHook(true);
    try {
      await hooksV2Api.create({
        title: newHookTitle.trim(),
        description: newHookDescription.trim(),
        stage_guidance: {},
        apply_criteria: {},
        hook_settings: {},
      });
      const hooksRes = await hooksV2Api.list();
      setHooks(normalizeArray(hooksRes.data));
      setShowNewHook(false);
      setNewHookTitle('');
      setNewHookDescription('');
    } finally {
      setSavingHook(false);
    }
  };

  if (redesignEnabled) {
    return (
      <>
        <AppShell role="teacher" title="Library" navItems={teacherV2NavItems('plan')}>
          <div className="space-y-4">
            <PageHeader
              eyebrow="Shared content"
              title="Library"
              subtitle="Create reusable activities, lessons, projects, and courses. Class availability is set on each item; this library is shared."
              actions={(
                <>
                  <Button
                    variant="secondary"
                    className={view === 'projects' ? undefined : 'hidden'}
                    onClick={() => setShowArchivedHooks((prev) => !prev)}
                  >
                    {showArchivedHooks ? 'Hide Archived' : 'Show Archived'}
                  </Button>
                  {view === 'projects' && <Button onClick={() => setShowNewHook(true)}>Create content</Button>}
                </>
              )}
            />

            <nav aria-label="Library content" className="flex flex-wrap gap-2">{[['courses', 'Courses'], ['units', 'Unit templates'], ['activities', 'Lessons & activities'], ['projects', 'Projects / Hooks']].map(([id, label]) => <button key={id} aria-pressed={view === id} className={view === id ? 'btn-primary' : 'btn-secondary'} onClick={() => { const go = () => setParams(current => { const next = new URLSearchParams(current); next.set('view', id); return next; }); if (workspace) workspace.requestLeave(go); else go(); }}>{label}</button>)}<Link className="btn-secondary" to="/v2/teacher/assessments">Assessment authoring</Link></nav>
            {view === 'activities' && <StandaloneLessons plans={lessonPlans} loading={loading} />}
            {(view === 'courses' || view === 'units') && <CurriculumLibrary view={view} />}
            {view === 'projects' && <>
            {loading ? (
              <SectionCard>
                <p className="text-sm text-muted">Loading...</p>
              </SectionCard>
            ) : (
              <div className="space-y-3">
                {visibleHooks.map((hook) => {
                  const plans = getPlansForHook(hook.id);
                  return (
                    <SectionCard
                      key={hook.id}
                      title={hook.title}
                      meta={`${plans.length} lesson plan${plans.length === 1 ? '' : 's'}`}
                      actions={(
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/v2/teacher/plan/hooks/${hook.id}`)}
                        >
                          Open Hook
                        </Button>
                      )}
                    >
                      {hook.description && (
                        <p className="text-sm text-muted">{hook.description}</p>
                      )}
                      {hook.is_archived && (
                        <p className="mt-1 text-xs text-warning">Archived</p>
                      )}
                    </SectionCard>
                  );
                })}
                {visibleHooks.length === 0 && (
                  <SectionCard>
                    <p className="text-sm text-muted">No hooks found.</p>
                  </SectionCard>
                )}
              </div>
            )}
            </>}
          </div>
        </AppShell>

        <Dialog
          open={showNewHook}
          onClose={setShowNewHook}
          title="Create Hook"
          footer={(
            <>
              <Button
                variant="secondary"
                onClick={() => setShowNewHook(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateHook}
                disabled={savingHook || !newHookTitle.trim()}
              >
                {savingHook ? 'Creating...' : 'Create Hook'}
              </Button>
            </>
          )}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text">Hook Title</label>
              <Input
                className="mt-1"
                value={newHookTitle}
                onChange={(e) => setNewHookTitle(e.target.value)}
                placeholder="e.g., Network Troubleshooting"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text">WHY / Context</label>
              <Textarea
                className="mt-1"
                rows={3}
                value={newHookDescription}
                onChange={(e) => setNewHookDescription(e.target.value)}
              />
            </div>
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <TeacherNav activeTab="plan" />
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Plan</h1>
            <p className="text-sm text-gray-500 mt-1">
              Create reusable lessons and project expectations. Organize class work in Learning plans.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-2 border border-gray-300 rounded text-sm"
              onClick={() => setShowArchivedHooks((prev) => !prev)}
            >
              {showArchivedHooks ? 'Hide Archived' : 'Show Archived'}
            </button>
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
              onClick={() => setShowNewHook(true)}
            >
              New Hook
            </button>
          </div>
        </div>

        <StandaloneLessons plans={lessonPlans} loading={loading} />
        {loading ? (
          <p className="mt-4 text-gray-500">Loading...</p>
        ) : (
          <div className="mt-6 space-y-3">
            {visibleHooks.map((hook) => {
              const plans = getPlansForHook(hook.id);
              return (
                <div key={hook.id} className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{hook.title}</h2>
                    {hook.description && (
                      <p className="text-sm text-gray-500 max-w-xl truncate">{hook.description}</p>
                    )}
                    {hook.is_archived && (
                      <p className="text-xs text-amber-600 mt-1">Archived</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{plans.length} lesson plan{plans.length === 1 ? '' : 's'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 border border-indigo-600 text-indigo-700 rounded text-sm"
                      onClick={() => navigate(`/v2/teacher/plan/hooks/${hook.id}`)}
                    >
                      Open Hook
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showNewHook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-lg font-semibold text-gray-900">Create Hook</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Hook Title</label>
                <input
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={newHookTitle}
                  onChange={(e) => setNewHookTitle(e.target.value)}
                  placeholder="e.g., Network Troubleshooting"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">WHY / Context</label>
                <textarea
                  className="mt-1 w-full border rounded px-3 py-2"
                  rows={3}
                  value={newHookDescription}
                  onChange={(e) => setNewHookDescription(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm"
                onClick={() => setShowNewHook(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
                onClick={handleCreateHook}
                disabled={savingHook || !newHookTitle.trim()}
              >
                {savingHook ? 'Creating...' : 'Create Hook'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default V2Plan;
